import root from "window-or-global";

import system from "@atg-shared/system";

const sharedCurityConfig = {
    tokenHandlerURI: root.clientConfig?.curity?.tokenHandlerURI,
    logoutURI: root.clientConfig?.curity?.logoutURI,
};

// TEMP (CLOUD_CURITY flag): Inline cloud URL resolution to avoid circular
// dependency (service-url → feature-experimentation → log → service-url).
// The other consumers use getCurityUri/getCurityTokenHandlerURI from
// @atg-authentication-shared/haapi-utils/src/cloudCurity — keep these in sync.
const CLOUD_CURITY_URLS: Partial<
    Record<string, {uri: string; tokenHandlerURI: string; logoutURI: string}>
> = {
    prod: {
        uri: "https://iam.prod.c1.atg.cloud",
        ...sharedCurityConfig,
    },
    qa: {
        uri: "https://iam.qa.c1.atg.dev",
        ...sharedCurityConfig,
    },
    test1: {
        uri: "https://iam-test1.devtest.c1.atg.dev",
        ...sharedCurityConfig,
    },
    test2: {
        uri: "https://iam-test2.devtest.c1.atg.dev",
        ...sharedCurityConfig,
    },
    test3: {
        uri: "https://iam-test3.devtest.c1.atg.dev",
        ...sharedCurityConfig,
    },
    stage: {
        uri: "https://iam.stage.c1.atg.dev",
        ...sharedCurityConfig,
    },
};

function isCloudCurityEnabled(): boolean {
    const result = root.optimizelyUserContext?.decide("cloud_curity");
    return (result?.variationKey ?? "off") === "on";
}

function isNewUserAuthEndpointEnabled(): boolean {
    const result = root.optimizelyUserContext?.decide("new_user_auth_endpoint");
    return (result?.variationKey ?? "off") === "on";
}

export function getCloudCurityUri(): string {
    if (system.isApp && root.clientConfig?.CURITY) {
        return root.clientConfig.CURITY;
    }
    const cloud = CLOUD_CURITY_URLS[root.clientConfig?.env];
    if (cloud && cloud.uri && isCloudCurityEnabled()) {
        return cloud.uri;
    }
    return root.clientConfig.curity.uri;
}

export function getCloudCurityTokenHandlerURI(): string {
    if (system.isApp && root.clientConfig?.TOKEN_HANDLER) {
        return root.clientConfig.TOKEN_HANDLER;
    }
    const cloud = CLOUD_CURITY_URLS[root.clientConfig?.env];
    if (cloud && cloud.tokenHandlerURI && isCloudCurityEnabled()) {
        return cloud.tokenHandlerURI;
    }
    return root.clientConfig.curity.tokenHandlerURI;
}

export function getCloudCurityLogoutURI(): string {
    if (system.isApp && root.clientConfig?.LOGOUT) {
        return root.clientConfig.LOGOUT;
    }
    const cloud = CLOUD_CURITY_URLS[root.clientConfig?.env];
    if (cloud && cloud.logoutURI && isCloudCurityEnabled()) {
        return cloud.logoutURI;
    }
    return root.clientConfig.curity.logoutURI;
}

export const SEARCH_ENVIRONMENT_URL =
    (typeof root !== "undefined" && root.clientConfig && root.clientConfig.searchUrl) ||
    "/search";

export const environment = root?.clientConfig?.env ?? "qa";
export const atgApiSubdomain = `api${environment !== "prod" ? `.${environment}.hh` : ""}`;
export const USER_SERVICE_URL = "/services/v1/user";
export const ACCOUNT_HISTORY_SERVICE_URL =
    "/services/tokenized-proxy/account-history/api/v1/account/transactions";
export const INFO_SERVICE_URL = "/services/v1/info";
export const PAYMENT_URL = "/services/tokenized-proxy/payment/api/v1";
export const PAYMENT_URL_NEW = "/services/tokenized-proxy/payment/api/v2";
export const PAYMENT_URL_V3 = "/services/tokenized-proxy/payment/api/v3";
export const BANK_ACCOUNT_URL = "/services/tokenized-proxy/bas/api/v1/bank-accounts";
export const PUBLIC_COUPON_SERVICE_URL = "/services/v1/coupons";
export const COUPON_SERVICE_URL =
    "/services/tokenized-proxy/horse-coupons/api/v1/coupons";
export const LOG_SERVICE_URL = "/services/v1/logs";
export const CUSTOMER_SERVICE_URL = "/services/v1/customer";
export const MEMBER_SERVICE_URL = "/services/v1/member";
export const NEW_MEMBER_SERVICE_URL = "/services/tokenized-proxy/member";
export const BET_SERVICE_URL = "/services/v1/bets";
export const BET_GALLERY_SERVICE_URL = "/services/v1/betGallery";
export const LEGACY_PRODUCT_SERVICE_URL = "/services/v1/products";
export const TEAM_SERVICE_LEGACY_URL = "/services/v1/teams";
export const TEAM_SERVICE_API_URL = "/services/tokenized-proxy/team/api";
export const TEAM_SERVICE_URL = `${TEAM_SERVICE_API_URL}/v1/teams`;
/**
 * @deprecated Legacy Tillsmmmans public api
 */
export const TEAM_SERVICE_PUBLIC_URL = "/services/api/v1/teams";
export const TEAM_CHAT_SERVICE_URL = `${TEAM_SERVICE_API_URL}/v2/chat`;
/**
 * @deprecated Legacy Tillsmmmans public api
 */
export const LEGACY_TEAM_CHAT_PUBLIC_SERVICE_URL = "/services/api/v2/chat";
export const TEAM_CHAT_PUBLIC_SERVICE_URL = "/team/api/v2/chat";
export const TEAM_SUBSCRIBE_SERVICE_URL = `${TEAM_SERVICE_API_URL}/v1/subscriptions`;
export const TEAM_PERSONALIZATION_SERVICE_URL = `${TEAM_SERVICE_API_URL}/v1/personalization`;
export const TEAM_MEMBER_SERVICE_URL = `${TEAM_SERVICE_API_URL}/v1/member`;
export const TEAM_CLIENT_LOG_SERVICE_URL = `${TEAM_SERVICE_API_URL}/v1/logs`;
export const SHOP_CHAT_SERVICE_URL = "/services/tokenized-proxy/shopshare/api/v1/chat";
export const SHOP_CHAT_SERVICE_PUBLIC_URL = "/services/shop-share/v1/chat";
export const SUBMAN_SERVICE_URL = "/services/tokenized-proxy/subman/api/v1";
export const SHOP_SERVICE_URL = "/services/v1/shops";
export const SHOP_SHARE_SERVICE_URL = "/services/shop-share/v1";
export const SHOP_SHARE_SERVICE_URL_TOKENIZED =
    "/services/tokenized-proxy/shopshare/api/v1";
export const COUPON_TEAM_SERVICE_URL =
    "/services/tokenized-proxy/couponteam/api/v1/coupon-team";
export const COUPON_TEAM_SERVICE_PUBLIC_URL = "/couponteam/api/v1/coupon-team";

export const MEMBER_AUTH_V2_URL = "/services/tokenized-proxy/member-auth/api/v2/member";
// remove when member-auth-v2 is out
export const MEMBER_AUTH_URL = "/services/tokenized-proxy/member-auth/api/v1/member";
export const USER_RECOVERY_URL = "/services/tokenized-proxy/member-auth/api/v1/recovery";

export const EMAIL_VERIFICATION_URL =
    "/services/tokenized-proxy/member-auth/api/v2/verification/email";

export const MEMBER_SERVICE_V3_URL = "/services/tokenized-proxy/member/api/v3/member";
export const MEMBER_SERVICE_V4_URL = "/services/tokenized-proxy/member/api/v4/member";
export const BALANCE_V3_URL = "/services/tokenized-proxy/wallet/api/v3/wallet/balance";
export const RGS_SERVICE_URL = "/services/tokenized-proxy/rgs/api/v1/rg";
export const KYC_SERVICE_URL = "/services/tokenized-proxy/kyc/api/v1/questionnaire";
export const VARENNE_COUPON_TEAMS_URL =
    "/services/tokenized-proxy/couponteam/api/v1/coupon-team";

export const getCurityConfig = () => {
    const tokenHandlerUrl =
        process.env.NODE_ENV !== "test"
            ? getCloudCurityTokenHandlerURI()
            : "https://login.qa.hh.atg.se/v1";
    const logoutServiceUrl =
        process.env.NODE_ENV !== "test"
            ? `${getCloudCurityLogoutURI()}?caller=${system.getSystemName()}`
            : `https://login.qa.hh.atg.se/v1/logout?caller=${system.getSystemName()}`;

    const url =
        process.env.NODE_ENV !== "test"
            ? getCloudCurityUri()
            : "https://iam.qa.hh.atg.se";

    return {
        logoutServiceUrl,
        tokenHandlerUrl,
        url,
    };
};

export const fetchEnvironment = () => root?.clientConfig?.env || "qa";
export const fetchAtgApiSubDomain = () => {
    const env = fetchEnvironment();
    return `api${env !== "prod" ? `.${env}.hh` : ""}`;
};

/**
 * Returns the URL for the `/user/auth` endpoint, applying the following priority:
 *
 * 1. `cloud_curity` ON  → cloud token-handler URL  (e.g. https://login.prod.c1.atg.cloud/v1/user/auth)
 * 2. `new_user_auth_endpoint` ON → legacy external token-handler URL  (e.g. https://login.atg.se/v1/user/auth)
 * 3. fallback → local proxy URL  (/services/v1/user/auth)
 */
export const getUserAuthUrl = (): string => {
    if (process.env.NODE_ENV === "test") {
        return "https://login.qa.hh.atg.se/v1/user/auth";
    }
    if (isCloudCurityEnabled()) {
        return `${getCloudCurityTokenHandlerURI()}/user/auth`;
    }
    if (isNewUserAuthEndpointEnabled()) {
        return `${root.clientConfig.curity.tokenHandlerURI}/user/auth`;
    }
    return `${USER_SERVICE_URL}/auth`;
};

// CMS
export const CMS_SERVICE_URL = "/services/cms";

// Rating Integration Service (risk score)
export const RIS_SERVICE_URL =
    "/services/tokenized-proxy/rating-integration-service/api/v1";

// RacingInfo
export const PRODUCT_SERVICE_URL = "/services/racinginfo/v1/api/products";
export const HORSE_SERVICE_URL = "/services/racinginfo/v1/api/horses";
export const HARRY_SERVICE_URL = "/services/racinginfo/v1/api/harry";
export const CALENDAR_SERVICE_URL = "/services/racinginfo/v1/api/calendar";
export const GAME_SERVICE_URL = "/services/racinginfo/v1/api/games";
export const TRACK_SERVICE_URL = "/services/racinginfo/v1/api/tracks";
export const RACE_SERVICE_URL = "/services/racinginfo/v1/api/races";
export const POOL_SERVICE_URL = "/services/racinginfo/v1/api/pools";
export const LIVE_STREAM_SERVICE_URL = "/services/racinginfo/v1/api/livestreams";
export const HIGHLIGHT_SERVICE_URL = "/services/racinginfo/v1/api/highlights";
export const HOTGAME_SERVICE_URL = "/services/racinginfo/v1/api/hotgames";
export const MEDIA_SERVICE_URL = "/services/racinginfo/v1/api/media";
export const SIMPLE_RESULTS_URL = "/services/racinginfo/v1/api/results";

// Harry subscription
export const HARRY_SUBSCRIPTION_URL =
    "/services/tokenized-proxy/harry-subscription/api/v1";
export const HARRY_SUBSCRIPTION_PUBLIC_URL = "/services/v1/api-public/products/harry";

// Elastic search services
export const SHOPS_SEARCH_SERVICE_URL = `${SEARCH_ENVIRONMENT_URL}/shops`;
export const VMV85_SEARCH_SERVICE_URL = `${SEARCH_ENVIRONMENT_URL}/vmiv85`;

// Vm Campaign
export const VM_SERVICE_URL = "/services/tokenized-proxy/horse-champions/api/v1";
export const VM_SERVICE_URL_PUBLIC = "horse-champions/api-public/v1";
export const VM_SERVICE_URL_PUBLIC_V2 = "horse-champions/api-public/v2";

export const LATEST_LOGIN_V2_URL =
    "/services/tokenized-proxy/member-auth/api/v2/member/login/latest";

// Member segmentation
export const MEMBER_SEGMENTATION_SERVICE_URL =
    "/services/tokenized-proxy/member-segmentation/api/v1/segmentation";

// RGS
export const RGS_NET_LOSS_LIMITS_SERVICE_URL = `${RGS_SERVICE_URL}/netlosslimit`;
export const RGS_SELF_EXCLUSION_SERVICE_URL = `${RGS_SERVICE_URL}/selfexclusion`;

// Big9
export const BIG9_COUPON_API_URL = "/services/tokenized/sports-coupon/api/v1/coupons";
export const BIG9_OFFERING_API_URL = "/services/sports-info-offering/api/v1/offering";
export const BIG9_PLACE_BET_API_URL = "/services/tokenized/sports-bet/api/v1/bet";
export const BIG9_PLACE_SELL_BET_V3_API_URL =
    "/services/tokenized/sports-bet/api/v3/bet/sell-bet";
export const BIG9_RECEIPTS_V3_API_URL = "/services/tokenized/sports-bet/api/v3/receipts";

// Big9 Partner Reduced Betting
export const BIG9_REDUCED_BETTING_URL = "/services/tokenized/reducedbet/api/v1/coupons";
export const BIG9_PLACE_REDUCED_BET_V3_API_URL =
    "/services/tokenized/sports-bet/api/v3/bet/reduced";

// Inbox
export const INBOX_SERVICE_URL = "/services/tokenized-proxy/customer-inbox/api/v1";

// Customer transactions
export const CUSTOMER_TRANSACTIONS_URL =
    "/services/tokenized-proxy/customer-transactions/api/v2";

// RCS, Responsible communication Services
export const RCS_SERVICE_URL = "/services/tokenized-proxy/rcs/api/v1";

// Horse betting placement
export const BET_PLACEMENT_V1_URL = "/services/tokenized-proxy/horse-betting/api/v1";
export const BET_PLACEMENT_V2_URL = "/services/tokenized-proxy/horse-betting/api/v2";

// Horse bet history
export const BET_HISTORY_OFFERING_BETS_METADATA =
    "/services/tokenized-proxy/horse-bet-history/api/v3/offering-bets-metadata";
export const BET_HISTORY_GRADING =
    "/services/tokenized-proxy/service-horse-bet-history/api/v1/grading";
export const BET_HISTORY_SINGLE_BETS_TODAY =
    "/services/tokenized-proxy/service-horse-bet-history/api/v1/single-bets/today";
export const BET_HISTORY_SINGLE_BETS_RESPONSES_TODAY =
    "/services/tokenized-proxy/service-horse-bet-history/api/v1/single-bet-responses/today";
export const HORSE_BET_HISTORY_V2_RECEIPTS =
    "/services/tokenized-proxy/horse-bet-history/api/v2/bet/receipts";
export const HORSE_BET_HISTORY_V3_RECEIPTS =
    "/services/tokenized-proxy/horse-bet-history/api/v3/bet/receipts";
export const BET_HISTORY_OFFERING_BETS =
    "/services/tokenized-proxy/horse-bet-history/api/v3/offering-bets";
export const BET_HISTORY_OFFERING_BETS_H2H_EVENTS =
    "/services/tokenized-proxy/horse-bet-history/api/v3/offering-bets/h2h/events";

// Quick links
export const QUICK_LINKS_SERVICE_URL = "/services/racinginfo/api/v2/highlights";

// Favorites upcoming starts
export const FAVORITE_UPCOMING_STARTS =
    "/services/tokenized-proxy/horse-favorites/api/v2/favorite/upcoming-starts";
export const HORSE_FAVORITES =
    "/services/tokenized-proxy/horse-favorites/api/v2/favorite";
export const HORSE_FAVORITES_PAGINATED =
    "/services/tokenized-proxy/horse-favorites/api/v3/favorite";
export const HORSE_FAVORITES_PREFERENCES_URL =
    "/services/tokenized-proxy/horse-favorites/api/v2/favorites/preferences";
export const HORSE_FAVORITES_BY_RACE =
    "/services/tokenized-proxy/horse-favorites/api/v3/favorite/by-race";

// Push Notifications
export const NOTIFICATIONS_SERVICE_URL = "/services/tokenized-proxy/notifications/api/v1";
